import { lazy, Suspense, useCallback, useMemo, useRef } from "react";
import { EditLocationPage } from "../../pages/location/editLocationPage.tsx";
import { EditKioskPage } from "../../pages/kiosk/editKioskPage.tsx";
import { ProductPopup } from "../../pages/product/searchEditProductPage.tsx";
import { CustomerEventsPage } from "../../pages/specialized/customerEventsPage.tsx";
import { TransactionDetailsPage } from "../../pages/specialized/transactionDetailsPage.tsx";
import { EditCustomerPage } from "../../pages/customer/editCustomerPage.tsx";
import { EditPlanogramDialog } from "../../pages/planogram/editPlanogramDialog.tsx";
import { EditCodeModal } from "../../pages/promotions/generateCodesPage.tsx";
import { PromotionModal } from "../../pages/promotions/searchEditPromotionPage.tsx";
import { EditReplenishModal } from "../../pages/replenish/editReplenishPage.tsx";
import { PackageDealEditor } from "../../pages/packageDeal/packageDealEditor.tsx";
import { EditEmailTemplatePage } from "../../pages/email/editEmailTemplatePage.tsx";
import { StatementInvoiceInfoModal } from "../../pages/statementConfig/statementInvoiceInfoModal.tsx";
import { PackageDealMode } from "../../api/packageDeal.ts";
import { GridEditModal } from "../editSearch/gridEditModal.tsx";
import { useSession } from "../../hooks/useSession.ts";
import type { InputParam } from "../../types/grid.ts";

const GenericGridPage = lazy(() =>
  import("../../pages/generic/genericGridPage.tsx").then((m) => ({ default: m.GenericGridPage })),
);

interface PagePopupProps {
  path: string;
  /** Child can pass `didChange=false` to suppress the parent grid refresh
   *  (e.g. read-only Customer Events view closed with no resolve/edit).
   *  Undefined means "refresh" — preserves legacy callers. */
  onClose: (didChange?: boolean) => void;
  /** Lets a popup-rendered component swap the popup target in place — e.g.
   *  EditCodeModal opening the Promotion modal via an in-modal link. The
   *  current popup unmounts and the new one mounts (no nested <dialog>s).
   *  Optional: callers without a path swap need (statementinvoiceinfo, etc.)
   *  pass undefined; recipients should gracefully no-op when missing. */
  onNavigate?: (path: string) => void;
}

/**
 * Dispatches a popup path (e.g. `editkiosk?edit=42`, `grid?name=foo`) to the
 * matching inline editor. Every dispatched target renders its own
 * `GridEditModal` — this wrapper just parses the path and routes. There is
 * intentionally no outer `<dialog>` chrome (see AGENTS.md § Create/Edit Modal
 * Guidelines — nesting native `<dialog>`s breaks focus/top-layer stacking).
 */
export function PagePopup({ path, onClose, onNavigate }: PagePopupProps) {
  const { cache } = useSession();
  const { pageName, editId, params } = useMemo(() => {
    const url = new URL(path, "http://localhost");
    const segments = url.pathname.split("/").filter(Boolean);
    return {
      pageName: segments[segments.length - 1]?.toLowerCase() ?? "",
      editId: url.searchParams.get("edit") ?? undefined,
      params: url.searchParams,
    };
  }, [path]);

  // Cache-derived promotions list (used by EditCodeModal when routed here via
  // hyperlink 7 / ad-hoc `generatecodes?edit=<id>` paths).
  const promotions = useMemo(() => {
    if (!cache?.Promotions) return [];
    return (cache.Promotions as Array<Record<string, unknown>>)
      .filter((p) => typeof p.PromotionID === "number" && typeof p.Title === "string")
      .map((p) => ({ PromotionID: p.PromotionID as number, Title: p.Title as string }));
  }, [cache]);

  // Centralised dirty tracking so child popups can signal whether the parent
  // grid needs a refresh.
  //   markDirty   — child calls when a save happens; never closes.
  //   handleClose — child calls to close; forwards accumulated dirty flag.
  //   handleCloseReadOnly — read-only popups always report "no change".
  // Pages that already chain `onSaved?.(); onClose();` keep working: the
  // first marks dirty, the second closes and reports the now-true flag.
  const dirtyRef = useRef(false);
  const markDirty = useCallback(() => { dirtyRef.current = true; }, []);
  const handleClose = useCallback(() => {
    // Forward the real dirty flag (boolean): true → refresh, false → skip.
    // Was `dirtyRef.current ? true : undefined`, but the grid treats `undefined`
    // as "refresh on every close" — so a clean close (no save) still reloaded
    // the grid, defeating the dirty tracking. EditCode close was the visible
    // symptom; this fixes every popup routed through handleClose.
    onClose(dirtyRef.current);
  }, [onClose]);
  // Save-and-close: pages that don't close themselves after save (PackageDeal,
  // EditCode, Product) rely on this to dismiss the popup + refresh the grid.
  const savedAndClose = useCallback(() => {
    dirtyRef.current = true;
    onClose(true);
  }, [onClose]);
  const handleCloseReadOnly = useCallback(() => onClose(false), [onClose]);

  // `grid?name=…&filters=<JSON InputParam[]>`: fixed filters for the popup grid
  // (e.g. hyperlink 8, Kiosk Version → Version details).
  const gridStaticFilters = useMemo((): InputParam[] | undefined => {
    const raw = params.get("filters");
    if (!raw) return undefined;
    try {
      const parsed: unknown = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as InputParam[]) : undefined;
    } catch {
      return undefined;
    }
  }, [params]);

  switch (pageName) {
    case "editkiosk":
      return <EditKioskPage popupEditId={editId} onClose={handleClose} onSaved={markDirty} />;
    case "editlocation":
      return <EditLocationPage popupEditId={editId} onClose={handleClose} onSaved={markDirty} />;
    case "editcustomer":
      return <EditCustomerPage popupEditId={editId} onClose={handleClose} onSaved={markDirty} />;
    case "searcheditproduct":
      // Product editors don't auto-close after save — use savedAndClose so
      // the popup dismisses and the grid refreshes in one step.
      return <ProductPopup editId={editId} onClose={handleClose} onSaved={savedAndClose} />;
    case "packagedeal": {
      const id = parseInt(editId ?? "0", 10);
      if (!Number.isFinite(id) || id <= 0) {
        return (
          <GridEditModal title="Package Deal" readOnly cancelLabel="Close" onClose={handleCloseReadOnly} onSave={() => { /* read-only */ }}>
            <p>Missing or invalid package deal id.</p>
          </GridEditModal>
        );
      }
      return <PackageDealEditor mode={PackageDealMode.Standard} dealId={id} onClose={handleClose} onSaved={savedAndClose} />;
    }
    case "subsidize": {
      // PriceType 36 = subsidy row; same S_PackageDeal table as `packagedeal`
      // but opened in Subsidize mode so the editor surfaces subsidy-specific
      // type/label affordances (see PackageDealEditor.modeLabel).
      const id = parseInt(editId ?? "0", 10);
      if (!Number.isFinite(id) || id <= 0) {
        return (
          <GridEditModal title="Subsidy" readOnly cancelLabel="Close" onClose={handleCloseReadOnly} onSave={() => { /* read-only */ }}>
            <p>Missing or invalid subsidy id.</p>
          </GridEditModal>
        );
      }
      return <PackageDealEditor mode={PackageDealMode.Subsidize} dealId={id} onClose={handleClose} onSaved={savedAndClose} />;
    }
    case "editreplenish":
      return <EditReplenishModal popupEditId={editId ?? params.get("id") ?? undefined} onClose={handleClose} onSaved={markDirty} />;
    case "editemailtemplate":
      return <EditEmailTemplatePage popupTemplateId={editId ?? params.get("id") ?? undefined} onClose={handleClose} onSaved={markDirty} />;
    case "editcustomerevents":
      // CustomerEventsPage tracks its own dirty flag internally and passes
      // `didChange` through `onClose(boolean)` — no centralised wrapping needed.
      return <CustomerEventsPage
        popupSid={params.get("sid") ?? undefined}
        popupTid={parseInt(params.get("tid") ?? "0", 10) || undefined}
        onClose={onClose}
      />;
    case "transactiondetails":
      // Read-only view — never refreshes the parent grid on close.
      return <TransactionDetailsPage
        popupTransactionId={parseInt(params.get("transactionId") ?? "0", 10) || undefined}
        onClose={handleCloseReadOnly}
      />;
    case "statementinvoiceinfo": {
      // Read-only invoice-info popup (statementAggregate Invoice Number, LinkID 199).
      const id = parseInt(params.get("id") ?? "0", 10);
      if (!Number.isFinite(id) || id <= 0) {
        return (
          <GridEditModal title="Invoice" readOnly cancelLabel="Close" onClose={handleCloseReadOnly} onSave={() => { /* read-only */ }}>
            <p>Missing or invalid invoice id.</p>
          </GridEditModal>
        );
      }
      return <StatementInvoiceInfoModal invoiceId={id} onClose={handleCloseReadOnly} />;
    }
    case "viewplanograms": {
      const id = parseInt(editId ?? "0", 10);
      return <EditPlanogramDialog open planogramId={Number.isFinite(id) ? id : -1} onClose={handleClose} onSaved={markDirty} />;
    }
    case "searcheditpromotion": {
      // Promotion hyperlink (LinkID 4) → edit the promotion in a modal.
      // PromotionModal keeps itself open after save (shows status), so wire
      // markDirty + handleClose: the grid refreshes on close only if saved.
      const id = parseInt(editId ?? "0", 10);
      if (!Number.isFinite(id) || id <= 0) {
        return (
          <GridEditModal title="Promotion" readOnly cancelLabel="Close" onClose={handleCloseReadOnly} onSave={() => { /* read-only */ }}>
            <p>Missing or invalid promotion id.</p>
          </GridEditModal>
        );
      }
      return <PromotionModal promotionId={id} onClose={handleClose} onSaved={markDirty} />;
    }
    case "generatecodes": {
      const id = parseInt(editId ?? "0", 10);
      if (!Number.isFinite(id) || id <= 0) {
        return (
          <GridEditModal title="Promotion code" readOnly cancelLabel="Close" onClose={handleCloseReadOnly} onSave={() => { /* read-only */ }}>
            <p>Missing or invalid promotion code id.</p>
          </GridEditModal>
        );
      }
      return <EditCodeModal
        codeId={id}
        promotions={promotions}
        onClose={handleClose}
        onSaved={savedAndClose}
        onOpenPromotion={onNavigate ? (pid) => onNavigate(`/page/searchEditPromotion?edit=${pid}`) : undefined}
      />;
    }
    case "grid": {
      const gridName = params.get("name") ?? "";
      return (
        <GridEditModal
          title={params.get("title") ?? (gridName ? `Grid — ${gridName}` : "Grid")}
          wide
          maxWidth="1400px"
          readOnly
          cancelLabel="Close"
          onClose={handleCloseReadOnly}
          onSave={() => { /* read-only */ }}
        >
          {gridName ? (
            <Suspense fallback={<p>Loading...</p>}>
              {gridStaticFilters ? (
                // Own persistKey so the popup's filters don't leak into the
                // standalone page's saved filter state, and vice versa.
                <GenericGridPage gridName={gridName} pageName={gridName}
                  staticFilters={gridStaticFilters} autoSearch persistKey={`${gridName}.popup`} />
              ) : (
                <GenericGridPage gridName={gridName} pageName={gridName} />
              )}
            </Suspense>
          ) : (
            <p>Missing grid name.</p>
          )}
        </GridEditModal>
      );
    }
    default:
      return (
        <GridEditModal
          title="Unsupported popup"
          readOnly
          cancelLabel="Close"
          onClose={handleCloseReadOnly}
          onSave={() => { /* read-only */ }}
        >
          <p>Unsupported popup page: {pageName}</p>
        </GridEditModal>
      );
  }
}
